
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBoardContext } from '@/contexts/BoardContext';
import { useBoardCollaboration } from '@/hooks/useBoardCollaboration';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Settings, Users, Mail } from 'lucide-react';
import { BoardMembersTab } from './board-settings/BoardMembersTab';
import { BoardInvitationsTab } from './board-settings/BoardInvitationsTab';
import { BoardGeneralTab } from './board-settings/BoardGeneralTab';

export const BoardSettings = () => {
  const navigate = useNavigate();
  const { currentBoardId, boards } = useBoardContext();
  const { canManageMembers } = useBoardCollaboration(currentBoardId);
  const [activeTab, setActiveTab] = useState('general');

  const currentBoard = boards?.find(board => board.id === currentBoardId);

  if (!currentBoard) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Board Not Found</h1>
            <Button onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Boards
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Board
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Board Settings</h1>
            <p className="text-gray-600 dark:text-gray-400">{currentBoard.title}</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Manage Board
            </CardTitle>
            <CardDescription>
              Configure your board settings, manage members, and send invitations.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general" className="flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  General
                </TabsTrigger>
                <TabsTrigger value="members" className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Members
                </TabsTrigger>
                <TabsTrigger value="invitations" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Invitations
                </TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="mt-6">
                <BoardGeneralTab board={currentBoard} />
              </TabsContent>

              <TabsContent value="members" className="mt-6">
                <BoardMembersTab boardId={currentBoardId!} />
              </TabsContent>

              <TabsContent value="invitations" className="mt-6">
                <BoardInvitationsTab boardId={currentBoardId!} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
